import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  FormLabel,
  Card,
  CardContent,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Divider,
  FormControl,
  Textarea,
  Select,
  Option,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/joy';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import {
  listShippingCompanies,
  getShippingCompany,
  createShippingCompany,
  updateShippingCompany,
  deleteShippingCompany,
} from '../../main/API/shippingCompany';
import PermissionGate from '../../main/components/PermissionGate';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { masterApiErrorMessage } from '../../main/utils/masterApiError';
import { getPaginatedMeta } from '../../main/utils/pagination';

const CODE_MAX = 64;

function buildPayload(form) {
  return {
    name: String(form.name || '').trim(),
    name_ar: form.name_ar != null ? String(form.name_ar).trim() : '',
    code: String(form.code || '').trim().slice(0, CODE_MAX),
    active: Boolean(form.active),
  };
}

const emptyForm = () => ({
  name: '',
  name_ar: '',
  code: '',
  active: true,
});

export default function ShippingCompaniesMasterScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [appliedActive, setAppliedActive] = useState('');
  const [ordering, setOrdering] = useState('name');
  const [appliedOrdering, setAppliedOrdering] = useState('name');

  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createErrors, setCreateErrors] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editErrors, setEditErrors] = useState({});

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const orderingOptions = useMemo(
    () => [
      { value: 'name', label: t('settingsPages.masterScreens.orderNameAz') },
      { value: '-name', label: t('settingsPages.masterScreens.orderNameZa') },
      { value: 'code', label: t('settingsPages.masterScreens.orderCodeAz') },
      { value: '-code', label: t('settingsPages.masterScreens.orderCodeZa') },
      { value: 'id', label: t('settingsPages.masterScreens.orderIdAsc') },
      { value: '-id', label: t('settingsPages.masterScreens.orderIdDesc') },
      { value: 'active', label: t('settingsPages.masterScreens.orderActiveFirst') },
      { value: '-active', label: t('settingsPages.masterScreens.orderInactiveFirst') },
    ],
    [t],
  );

  const load = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.SHIPPING_COMPANIES_LIST)) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ordering: appliedOrdering,
      };
      if (appliedSearch.trim()) params.search = appliedSearch.trim();
      if (appliedActive === 'true' || appliedActive === 'false') {
        params.active = appliedActive === 'true';
      }

      const response = await listShippingCompanies(params);
      const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
      setData(meta);
      setRows(meta.results);
    } catch (error) {
      console.error('Failed to load shipping companies:', error);
      setResponseMessage(masterApiErrorMessage(error, t) || t('settingsPages.shippingCompaniesMaster.loadFailed'));
      setFailedNotification(true);
      setRows([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, appliedSearch, appliedActive, appliedOrdering, t]);

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, appliedSearch, appliedActive, appliedOrdering]);

  const applyFilters = (e) => {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setAppliedSearch(search);
    setAppliedActive(filterActive);
    setAppliedOrdering(ordering);
  };

  const openDetail = async (row) => {
    if (!hasPermission(PERMISSIONS.SHIPPING_COMPANIES_VIEW)) {
      setDetailRow(row);
      setDetailOpen(true);
      return;
    }
    setDetailLoading(true);
    setDetailOpen(true);
    setDetailRow(null);
    try {
      const res = await getShippingCompany(row.id);
      setDetailRow(res.data);
    } catch (error) {
      setDetailRow(row);
      setResponseMessage(masterApiErrorMessage(error, t) || t('settingsPages.shippingCompaniesMaster.detailLoadFailed'));
      setFailedNotification(true);
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreate = () => {
    setCreateForm(emptyForm());
    setCreateErrors({});
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      name: row.name || '',
      name_ar: row.name_ar != null ? row.name_ar : '',
      code: row.code != null ? row.code : '',
      active: row.active !== false,
    });
    setEditErrors({});
    setEditOpen(true);
    setDetailOpen(false);
  };

  const validateForm = (payload) => {
    const errors = {};
    if (!payload.name) errors.name = t('settingsPages.masterScreens.nameEnglishRequired');
    if (payload.code.length > CODE_MAX) errors.code = t('settingsPages.masterScreens.codeMaxLength', { max: CODE_MAX });
    return errors;
  };

  const submitCreate = async () => {
    if (!hasPermission(PERMISSIONS.SHIPPING_COMPANIES_CREATE)) return;
    const payload = buildPayload(createForm);
    const errs = validateForm(payload);
    setCreateErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await createShippingCompany(payload);
      setResponseMessage(t('settingsPages.shippingCompaniesMaster.createdSuccess'));
      setSuccessNotification(true);
      setCreateOpen(false);
      load();
    } catch (error) {
      const d = error.response && error.response.data;
      if (error.response && error.response.status === 400 && d && typeof d === 'object' && !d.detail) {
        const fieldErr = {};
        Object.keys(d).forEach((k) => {
          const v = d[k];
          fieldErr[k] = Array.isArray(v) ? v[0] : String(v);
        });
        setCreateErrors(fieldErr);
      }
      const msg = masterApiErrorMessage(error, t) || t('settingsPages.shippingCompaniesMaster.createFailed');
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const submitEdit = async () => {
    if (!editId) return;
    if (!hasPermission(PERMISSIONS.SHIPPING_COMPANIES_UPDATE)) return;
    const payload = buildPayload(editForm);
    const errs = validateForm(payload);
    setEditErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await updateShippingCompany(editId, payload, true);
      setResponseMessage(t('settingsPages.shippingCompaniesMaster.updatedSuccess'));
      setSuccessNotification(true);
      setEditOpen(false);
      setEditId(null);
      load();
    } catch (error) {
      const d = error.response && error.response.data;
      if (error.response && error.response.status === 400 && d && typeof d === 'object' && !d.detail) {
        const fieldErr = {};
        Object.keys(d).forEach((k) => {
          const v = d[k];
          fieldErr[k] = Array.isArray(v) ? v[0] : String(v);
        });
        setEditErrors(fieldErr);
      }
      const msg = masterApiErrorMessage(error, t) || t('settingsPages.shippingCompaniesMaster.updateFailed');
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (row) => {
    setDeleteError('');
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    if (!hasPermission(PERMISSIONS.SHIPPING_COMPANIES_DELETE)) return;
    setDeleteLoading(true);
    try {
      await deleteShippingCompany(deleteTarget.id);
      setDeleteError('');
      setResponseMessage(t('settingsPages.shippingCompaniesMaster.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteOpen(false);
      setDeleteTarget(null);
      setDetailOpen(false);
      load();
    } catch (error) {
      const msg = masterApiErrorMessage(error, t) || t('settingsPages.shippingCompaniesMaster.deleteFailed');
      setDeleteError(msg);
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const canCreate = hasPermission(PERMISSIONS.SHIPPING_COMPANIES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.SHIPPING_COMPANIES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.SHIPPING_COMPANIES_DELETE);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('settingsPages.masterScreens.colId'), width: 72 },
      { field: 'name', headerName: t('settingsPages.masterScreens.colNameEn'), flex: 1.5, minWidth: 180 },
      { field: 'name_ar', headerName: t('settingsPages.masterScreens.colNameAr'), flex: 1.2, minWidth: 140 },
      { field: 'code', headerName: t('settingsPages.masterScreens.colCode'), flex: 1, minWidth: 120 },
      {
        field: 'active',
        headerName: t('settingsPages.masterScreens.colActive'),
        width: 100,
        renderCell: (params) =>
          params.value ? (
            <Chip size="sm" variant="soft" color="success">
              {t('actions.yes')}
            </Chip>
          ) : (
            <Chip size="sm" variant="soft" color="neutral">
              {t('actions.no')}
            </Chip>
          ),
      },
      {
        field: 'actions',
        headerName: t('settingsPages.masterScreens.colActions'),
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canUpdate && (
              <Tooltip title={t('settingsPages.masterScreens.tooltipEdit')}>
                <IconButton size="sm" variant="soft" color="primary" onClick={() => openEdit(params.row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('settingsPages.masterScreens.tooltipDelete')}>
                <IconButton size="sm" variant="soft" color="danger" onClick={() => confirmDelete(params.row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, canUpdate, canDelete],
  );

  const rowCount = data && data.count != null ? data.count : rows.length;

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ display: 'flex' }} m={3}>
        <Box width="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography component="nav" level="body-sm" sx={{ mb: 1 }}>
                <Link color="neutral" href="/settings/general">{t('nav.settings')}</Link>
                {' / '}
                <Typography component="span" sx={{ color: 'text.secondary' }}>
                  {t('settingsPages.shippingCompaniesMaster.breadcrumbMaster')}
                </Typography>
              </Typography>
              <Typography textAlign="start" level="h1" sx={{ fontSize: { xs: '1.5rem', sm: 'inherit' } }}>
                {t('settingsPages.shippingCompaniesMaster.pageTitle')}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5, maxWidth: 720 }}>
                {t('settingsPages.shippingCompaniesMaster.introPrefix')}
                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                  shipping_company
                </Typography>
                {t('settingsPages.shippingCompaniesMaster.introSuffix')}
              </Typography>
            </Box>
            {canCreate && (
              <Button startDecorator={<AddIcon />} onClick={openCreate}>
                {t('settingsPages.shippingCompaniesMaster.btnNew')}
              </Button>
            )}
          </Stack>

          <PermissionGate
            permissions={[PERMISSIONS.SHIPPING_COMPANIES_LIST]}
            fallback={
              <Typography sx={{ p: 2 }}>{t('settingsPages.shippingCompaniesMaster.noListPermission')}</Typography>
            }
          >
            <Box component="form" onSubmit={applyFilters} sx={{ mb: 2 }}>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid xs={12} sm={6} md={3}>
                  <FormLabel>{t('settingsPages.masterScreens.searchLabelNameArCode')}</FormLabel>
                  <Input
                    placeholder={t('settingsPages.masterScreens.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={2}>
                  <FormLabel>{t('settingsPages.masterScreens.filterActive')}</FormLabel>
                  <Select
                    value={filterActive}
                    onChange={(_, v) => setFilterActive(v ?? '')}
                  >
                    <Option value="">{t('settingsPages.masterScreens.filterAll')}</Option>
                    <Option value="true">{t('settingsPages.masterScreens.filterActiveOnly')}</Option>
                    <Option value="false">{t('settingsPages.masterScreens.filterInactiveOnly')}</Option>
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormLabel>{t('settingsPages.masterScreens.orderingLabel')}</FormLabel>
                  <Select value={ordering} onChange={(_, v) => setOrdering(v || 'name')}>
                    {orderingOptions.map((o) => (
                      <Option key={o.value} value={o.value}>{o.label}</Option>
                    ))}
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={2}>
                  <Button type="submit">{t('actions.apply')}</Button>
                </Grid>
              </Grid>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: '12px' }}>
              <CardContent sx={{ p: 0 }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  getRowId={(r) => r.id}
                  paginationMode="server"
                  rowCount={rowCount}
                  pageSizeOptions={[25, 50, 100]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  disableRowSelectionOnClick
                  onRowDoubleClick={(params) => openDetail(params.row)}
                  autoHeight
                  sx={{ border: 'none' }}
                />
              </CardContent>
            </Card>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
              {t('settingsPages.masterScreens.doubleClickFooterCompanies')}
            </Typography>
          </PermissionGate>

          <Snackbar
            open={failedNotification}
            color="danger"
            variant="soft"
            onClose={() => setFailedNotification(false)}
            autoHideDuration={10000}
          >
            <Typography level="body-sm" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
              {t('settingsPages.masterScreens.snackbarErrorTitle')}
            </Typography>
            <Typography level="body-sm">{responseMessage}</Typography>
          </Snackbar>
          <Snackbar
            open={successNotification}
            color="success"
            variant="soft"
            onClose={() => setSuccessNotification(false)}
            autoHideDuration={4000}
          >
            {responseMessage}
          </Snackbar>
        </Box>
      </Box>

      <Modal open={detailOpen} onClose={() => setDetailOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480 }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 1 }}>
            {t('settingsPages.shippingCompaniesMaster.detailTitle')}
          </Typography>
          {detailLoading && <Typography level="body-sm">{t('settingsPages.masterScreens.loading')}</Typography>}
          {!detailLoading && detailRow && (
            <>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.masterScreens.colId')}</Typography><Typography>{detailRow.id}</Typography></Box>
                <Divider />
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.masterScreens.colNameEn')}</Typography><Typography>{detailRow.name || t('settingsPages.masterScreens.dash')}</Typography></Box>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.masterScreens.colNameAr')}</Typography><Typography dir="rtl">{detailRow.name_ar || t('settingsPages.masterScreens.dash')}</Typography></Box>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.masterScreens.colCode')}</Typography><Typography sx={{ fontFamily: 'monospace' }}>{detailRow.code || t('settingsPages.masterScreens.dash')}</Typography></Box>
                <Box><Typography level="body-xs" sx={{ color: 'neutral.600' }}>{t('settingsPages.masterScreens.colActive')}</Typography><Typography>{detailRow.active !== false ? t('actions.yes') : t('actions.no')}</Typography></Box>
              </Stack>
              <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                {canUpdate && <Button onClick={() => openEdit(detailRow)}>{t('actions.edit')}</Button>}
                {canDelete && (
                  <Button color="danger" variant="soft" onClick={() => confirmDelete(detailRow)}>
                    {t('actions.delete')}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 2 }}>{t('settingsPages.shippingCompaniesMaster.modalNewTitle')}</Typography>
          <Stack spacing={1.5}>
            <FormControl required error={!!createErrors.name}>
              <FormLabel>{t('settingsPages.masterScreens.labelNameEn')}</FormLabel>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
              />
              {createErrors.name && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{createErrors.name}</Typography>
              )}
            </FormControl>
            <FormControl error={!!createErrors.name_ar}>
              <FormLabel>{t('settingsPages.masterScreens.labelNameAr')}</FormLabel>
              <Textarea
                minRows={2}
                value={createForm.name_ar}
                onChange={(e) => setCreateForm((p) => ({ ...p, name_ar: e.target.value }))}
              />
            </FormControl>
            <FormControl error={!!createErrors.code}>
              <FormLabel>{t('settingsPages.masterScreens.labelCodeOptional', { max: CODE_MAX })}</FormLabel>
              <Input
                value={createForm.code}
                onChange={(e) => setCreateForm((p) => ({ ...p, code: e.target.value.slice(0, CODE_MAX) }))}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
              {createErrors.code && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{createErrors.code}</Typography>
              )}
            </FormControl>
            <NativeCheckbox
              checked={createForm.active}
              onChange={(e) => setCreateForm((p) => ({ ...p, active: e.target.checked }))}
              label={t('settingsPages.masterScreens.labelActive')}
            />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={() => setCreateOpen(false)}>{t('actions.cancel')}</Button>
              <Button loading={loading} onClick={submitCreate}>{t('settingsPages.masterScreens.btnCreate')}</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 2 }}>{t('settingsPages.shippingCompaniesMaster.modalEditTitle', { id: editId })}</Typography>
          <Stack spacing={1.5}>
            <FormControl required error={!!editErrors.name}>
              <FormLabel>{t('settingsPages.masterScreens.labelNameEn')}</FormLabel>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              />
              {editErrors.name && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{editErrors.name}</Typography>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>{t('settingsPages.masterScreens.labelNameAr')}</FormLabel>
              <Textarea
                minRows={2}
                value={editForm.name_ar}
                onChange={(e) => setEditForm((p) => ({ ...p, name_ar: e.target.value }))}
              />
            </FormControl>
            <FormControl error={!!editErrors.code}>
              <FormLabel>{t('settingsPages.masterScreens.labelCodeOptional', { max: CODE_MAX })}</FormLabel>
              <Input
                value={editForm.code}
                onChange={(e) => setEditForm((p) => ({ ...p, code: e.target.value.slice(0, CODE_MAX) }))}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
              {editErrors.code && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>{editErrors.code}</Typography>
              )}
            </FormControl>
            <NativeCheckbox
              checked={editForm.active}
              onChange={(e) => setEditForm((p) => ({ ...p, active: e.target.checked }))}
              label={t('settingsPages.masterScreens.labelActive')}
            />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={() => setEditOpen(false)}>{t('actions.cancel')}</Button>
              <Button loading={loading} onClick={submitEdit}>{t('actions.save')}</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={deleteOpen} onClose={() => {
        if (deleteLoading) return;
        setDeleteOpen(false);
        setDeleteError('');
      }}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <Typography level="title-lg">{t('settingsPages.shippingCompaniesMaster.deleteTitle')}</Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>
            {deleteTarget && (
              <>
                <Typography component="span" sx={{ fontWeight: 600 }}>{deleteTarget.name || t('settingsPages.masterScreens.dash')}</Typography>
                {deleteTarget.code ? (
                  <>
                    {' '}
                    <Typography component="span" sx={{ fontFamily: 'monospace' }}>({deleteTarget.code})</Typography>
                  </>
                ) : null}
              </>
            )}
          </Typography>
          {deleteError ? (
            <Typography level="body-sm" color="danger" sx={{ mt: 1.5, p: 1.5, borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
              {deleteError}
            </Typography>
          ) : null}
          <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 1 }}>
            {t('settingsPages.masterScreens.deleteBlockedCompany')}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" disabled={deleteLoading} onClick={() => { setDeleteOpen(false); setDeleteError(''); }}>{t('actions.cancel')}</Button>
            <Button color="danger" loading={deleteLoading} onClick={runDelete}>{t('actions.delete')}</Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </DirectionalMaterialThemeProvider>
  );
}
